/**
 * CoPilotGhostText — TipTap extension that renders an externally-provided
 * AI draft as faded ghost-text inside an empty editor.
 *
 * Lifecycle:
 *   - React pushes the draft text via `editor.commands.setCoPilotGhost(text)`.
 *   - The decoration renders only while the editor doc is empty AND text is set.
 *   - Tab accepts: clears ghost, inserts the text, fires `onAccept(text)`.
 *   - Escape dismisses: clears ghost, fires `onDismiss()`.
 *   - Typing replaces: any user-driven doc change that leaves the doc non-empty
 *     auto-clears the ghost and fires `onDismiss()`.
 *
 * The "external source" model differs from the sibling `GhostSuggestions`
 * extension (which is character-triggered). Co-pilot drafts arrive from a
 * streaming SSE in React state; the editor is purely a renderer + acceptor.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { escapeHtml } from '@airclou/utils';

// One-shot style injection: suppress TipTap's Placeholder pseudo on any empty
// paragraph that currently contains our ghost-text widget. Without this both
// the placeholder ("Type your reply…") and the ghost-text render in the same
// position. We rely on `:has()` (Baseline-supported in modern browsers).
if (typeof document !== 'undefined' && !document.head.querySelector('style[data-copilot-ghost-styles]')) {
  const style = document.createElement('style');
  style.setAttribute('data-copilot-ghost-styles', '');
  style.textContent = `
    .ProseMirror p:has([data-copilot-ghost])::before {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

export type CoPilotGhostMode = 'accept' | 'retry';

export interface CoPilotGhostTextOptions {
  /** Fired when the agent presses Tab to accept an `accept`-mode suggestion. */
  onAccept?: (text: string) => void;
  /** Fired when the agent dismisses (Esc) or types over the suggestion. */
  onDismiss?: () => void;
  /**
   * Fired when the agent presses Tab on a `retry`-mode suggestion. The ghost
   * is cleared automatically before this fires; the caller is responsible for
   * kicking off a new request and pushing fresh ghost text when ready.
   */
  onRetry?: () => void;
}

interface PluginState {
  text: string | null;
  mode: CoPilotGhostMode;
}

interface SetCoPilotGhostOptions {
  /**
   * 'accept' (default): Tab inserts `text` into the editor.
   * 'retry':            Tab fires `onRetry` instead of inserting; the text is
   *                     shown verbatim as an explanation/prompt (e.g. "Draft
   *                     failed — press Tab to retry"). Use this when the
   *                     ghost slot is repurposed as an inline error surface.
   */
  mode?: CoPilotGhostMode;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    coPilotGhostText: {
      /** Push (or clear, with `null`) the ghost-text suggestion. */
      setCoPilotGhost: (
        text: string | null,
        options?: SetCoPilotGhostOptions,
      ) => ReturnType;
    };
  }
}

export const coPilotGhostPluginKey = new PluginKey<PluginState>('coPilotGhostText');

function isDocEmpty(doc: { childCount: number; firstChild: { content: { size: number } } | null }): boolean {
  return doc.childCount === 1 && (doc.firstChild?.content.size ?? 0) === 0;
}

/**
 * Split plain-text ghost-text into the same paragraph slices used both by
 * the rendered ghost decoration and by `insertContent` on Tab-accept. The
 * two surfaces share this so the ghost preview and the inserted output
 * render at pixel-identical positions — no UI shift on accept.
 *
 * Splits on any run of newlines (single or double); each non-empty line
 * becomes one paragraph. Trim is intentional — LLM output often has
 * trailing spaces that would otherwise leak into the editor.
 */
function splitParagraphs(text: string): string[] {
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Convert plain-text ghost-text into HTML paragraphs for `insertContent`.
 * Each non-empty line becomes its own `<p>` (matches the ghost rendering
 * which uses block-spans with the same per-line margin).
 */
function plainTextToParagraphs(text: string): string {
  const paragraphs = splitParagraphs(text).map((line) => `<p>${escapeHtml(line)}</p>`);
  return paragraphs.length > 0 ? paragraphs.join('') : `<p>${escapeHtml(text)}</p>`;
}

export const CoPilotGhostText = Extension.create<CoPilotGhostTextOptions>({
  name: 'coPilotGhostText',

  addOptions() {
    return {
      onAccept: undefined,
      onDismiss: undefined,
    };
  },

  addCommands() {
    return {
      setCoPilotGhost: (text, options) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(coPilotGhostPluginKey, {
            type: 'set',
            text: text || null,
            mode: options?.mode ?? 'accept',
          });
          dispatch(tr);
        }
        return true;
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const ps = coPilotGhostPluginKey.getState(this.editor.state);
        if (!ps?.text || !this.editor.isEmpty) return false;
        const text = ps.text;
        const mode = ps.mode;
        // Clear ghost first so neither acceptance nor a retry-followup
        // flickers alongside the stale decoration in the same paint.
        this.editor.commands.setCoPilotGhost(null);
        if (mode === 'retry') {
          this.options.onRetry?.();
        } else {
          // Insert as HTML paragraphs so multi-line drafts keep their breaks.
          // `insertContent(string)` treats a bare string as plain text and
          // collapses everything to a single paragraph.
          this.editor.commands.insertContent(plainTextToParagraphs(text));
          this.options.onAccept?.(text);
        }
        return true;
      },
      Escape: () => {
        const ps = coPilotGhostPluginKey.getState(this.editor.state);
        if (!ps?.text) return false;
        this.editor.commands.setCoPilotGhost(null);
        this.options.onDismiss?.();
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const extension = this;
    return [
      new Plugin<PluginState>({
        key: coPilotGhostPluginKey,

        state: {
          init() {
            return { text: null, mode: 'accept' };
          },
          apply(tr, oldState, _oldEditorState, newEditorState) {
            const meta = tr.getMeta(coPilotGhostPluginKey);
            if (meta?.type === 'set') {
              return { text: meta.text ?? null, mode: meta.mode ?? 'accept' };
            }
            // User typed (or otherwise made the doc non-empty): auto-dismiss.
            // We notify React asynchronously so we don't dispatch during apply.
            if (oldState.text && tr.docChanged && !isDocEmpty(newEditorState.doc)) {
              queueMicrotask(() => extension.options.onDismiss?.());
              return { text: null, mode: oldState.mode };
            }
            return oldState;
          },
        },

        props: {
          decorations(state) {
            const ps = coPilotGhostPluginKey.getState(state);
            if (!ps?.text) return DecorationSet.empty;
            if (!isDocEmpty(state.doc)) {
              return DecorationSet.empty;
            }

            // Position 1 = inside the first (empty) paragraph, before the cursor.
            return DecorationSet.create(state.doc, [
              Decoration.widget(
                1,
                () => {
                  // Render each paragraph as a block-level span with the same
                  // top margin TipTap's <p> nodes get on accept. The editor
                  // wraps content in `prose prose-sm` (Tailwind Typography),
                  // which sets `<p>` margin-top to 1.1428571em (8/7em); the
                  // editor's CSS then overrides the first <p> to 0. Match
                  // both so the ghost preview and the post-accept content
                  // sit at pixel-identical positions.
                  const wrapper = document.createElement('span');
                  wrapper.setAttribute('data-copilot-ghost', 'true');
                  // `display: contents` — the wrapper exists in DOM (so the
                  // [data-copilot-ghost] selector keeps firing for placeholder
                  // suppression) but doesn't generate a box. Without this,
                  // the inline span's line-box adds an extra strut at the
                  // top of the <p>, pushing every block-child down by one
                  // baseline and creating a visible drift on Tab-accept.
                  wrapper.style.cssText =
                    'display: contents; color: #9ca3af; font-style: italic; pointer-events: none; user-select: none;';
                  const text = ps.text ?? '';
                  const lines = splitParagraphs(text);
                  if (lines.length === 0) {
                    // Whitespace-only fallback — render verbatim so it's
                    // visible at all rather than disappearing.
                    wrapper.style.whiteSpace = 'pre-wrap';
                    wrapper.textContent = text;
                  } else {
                    lines.forEach((line, i) => {
                      const blk = document.createElement('span');
                      // 1.1428571em = 8/7em, matching `prose-sm`'s <p> margin.
                      blk.style.cssText = `display: block; white-space: pre-wrap; margin-top: ${i === 0 ? '0' : '1.1428571em'};`;
                      blk.textContent = line;
                      wrapper.appendChild(blk);
                    });
                  }
                  return wrapper;
                },
                { side: 1, ignoreSelection: true }
              ),
            ]);
          },
        },
      }),
    ];
  },
});
